import {
  type ConsentCategory,
  type ConsentCookieOptions,
  categories,
  defaultOptions,
} from './constants'
import { userHasEnabledGlobalPrivacyControl } from './gpc'
import { parseConsentCookies } from './parse'
import { serializeBooleanCookie } from './serialize'

export { categories } from './constants'
export type { ConsentCategory, ConsentCookieOptions } from './constants'

/**
 * Represents a set of cookies that store the user's consent preferences.
 *
 * All of the methods on this class are immutable, and will return a new
 * instance instead of mutating the current instance. This allows for easy
 * chaining of methods, ensures that the original instance remains unchanged,
 * and allows detecting changes through object equality checks.
 *
 * @example
 * const cookie = ConsentPreferencesCookie.from(document.cookie)
 * const updatedCookie = cookie.withConsent('analytics', true)
 * assert(updatedCookie !== cookie)
 *
 * const sameChangeAgain = updatedCookie.withConsent('analytics', true)
 * assert(sameChangeAgain === updatedCookie)
 */
export class ConsentPreferencesCookie {
  /**
   * Creates a new {@link ConsentPreferencesCookie} from a header/document.cookie
   * This method is responsible for parsing the cookie string.
   */
  public static from(
    cookie?: string | null,
    options: ConsentCookieOptions = {},
  ): ConsentPreferencesCookie {
    const cookieNames = {
      ...defaultOptions.cookieNames,
      ...(options.cookieNames ?? {}),
    }
    const fullOptions = { ...defaultOptions, ...options, cookieNames }

    const { allowed, acknowledged } = parseConsentCookies(cookie, cookieNames)
    if (acknowledged) allowed.add('required')

    const acknowledgedViaGpc =
      fullOptions.respectGlobalPrivacyControl &&
      userHasEnabledGlobalPrivacyControl()

    return new ConsentPreferencesCookie(
      fullOptions,
      allowed,
      acknowledged || acknowledgedViaGpc,
    )
  }

  protected constructor(
    /** Options required for reading/writing cookies */
    public readonly options: Required<ConsentCookieOptions>,
    /** A set of consents the user has granted */
    public readonly allowed: ReadonlySet<ConsentCategory>,
    /** Whether the user has acknowledged the cookie banner */
    public readonly acknowledged: boolean,
  ) {}

  /**
   * Returns a new instance with a specific consent allowed/rejected.
   * Marks the cookie banner as acknowledged
   */
  public withConsent(category: ConsentCategory, allowed: boolean): this {
    if (this.allowed.has(category) === allowed && this.acknowledged) {
      return this
    }

    const consents = new Set(this.allowed)
    consents[allowed ? 'add' : 'delete'](category)
    consents.add('required')
    return new ConsentPreferencesCookie(this.options, consents, true) as this
  }

  /**
   * Returns a new instance with only the specific consents allowed.
   * Marks the cookie banner as acknowledged
   */
  public allowingOnly(allowed: Iterable<ConsentCategory>): this {
    const consents = new Set<ConsentCategory>(['required'])
    for (const category of allowed) consents.add(category)
    return new ConsentPreferencesCookie(this.options, consents, true) as this
  }

  /**
   * Returns a new instance with all of the consent categories accepted
   * Marks the cookie banner as acknowledged
   */
  public allowingAll(): this {
    if (this.acknowledged && this.allowed.size === categories.length) {
      return this
    }

    const next = new Set(this.allowed)
    for (const category of categories) next.add(category)

    return new ConsentPreferencesCookie(this.options, next, true) as this
  }
  /**
   * Returns a new instance with all of the non-essential consent categories rejected
   * Marks the cookie banner as acknowledged
   */
  public rejectingAll(): this {
    if (!this.allowed.size && this.acknowledged) {
      return this
    }

    return new ConsentPreferencesCookie(
      this.options,
      new Set(['required']),
      true,
    ) as this
  }

  /**
   * Serializes the consent preferences to an array of `Set-Cookie` headers
   */
  public toSetCookieHeaders() {
    const { cookieNames, ...options } = this.options
    return [
      serializeBooleanCookie(
        cookieNames.acknowledged,
        this.acknowledged,
        options,
      ),
      ...categories.flatMap((category) =>
        cookieNames[category].map((cookieName) =>
          serializeBooleanCookie(
            cookieName,
            this.allowed.has(category),
            options,
          ),
        ),
      ),
    ]
  }

  /**
   * Persists the consent preferences to `document.cookie`
   */
  public saveToDocumentCookie() {
    for (const cookie of this.toSetCookieHeaders()) {
      document.cookie = cookie
    }
  }

  /**
   * Persists the consent preferences by appending a `Set-Cookie` header to a
   * supplied {@link Headers} object.
   */
  public appendTo(headers: Headers) {
    for (const cookie of this.toSetCookieHeaders()) {
      headers.append('Set-Cookie', cookie)
    }
  }
}
